import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  Input,
  CodeBlock,
  Spinner,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert } from '@vnetwork/ui/feedback';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useSetupMfaOtp,
  useVerifyMfaOtp,
  accountErrorMessage,
  type OtpSetup,
} from '@/api/account';
import styles from './Information.module.css';

interface OtpSetupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Authenticator-app (TOTP) enrolment. On open it provisions a secret + QR +
 * backup codes; the user scans, saves the codes, and confirms with the first
 * 6-digit code to enable.
 */
export function OtpSetupDialog({ open, onOpenChange }: OtpSetupDialogProps) {
  const { show } = useToast();
  const [setupData, setSetupData] = useState<OtpSetup | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const setup = useSetupMfaOtp({
    onSuccess: (data) => setSetupData(data),
    onError: (e) =>
      setError(accountErrorMessage(e, 'Could not start authenticator setup.')),
  });

  const verify = useVerifyMfaOtp({
    onSuccess: () => {
      show({ tone: 'success', title: 'Authenticator app enabled' });
      onOpenChange(false);
    },
    onError: (e) =>
      setError(accountErrorMessage(e, 'That code did not match. Try again.')),
  });

  // Provision once when the dialog opens; reset everything when it closes.
  useEffect(() => {
    if (open) {
      setSetupData(null);
      setCode('');
      setError(null);
      setup.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function handleVerify() {
    if (!setupData) return;
    if (code.trim().length !== 6) {
      setError('Enter the 6-digit code from your authenticator app.');
      return;
    }
    setError(null);
    verify.mutate({
      secret: setupData.secret,
      otp_code: code.trim(),
      backup_codes: setupData.backup_codes,
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>Set up authenticator app</DialogTitle>
          <DialogDescription>
            Scan the QR code with Google Authenticator, 1Password, or any TOTP app.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          {setup.isPending || !setupData ? (
            <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--vn-space-8)' }}>
              <Spinner />
            </div>
          ) : (
            <div className={styles.otpSetup}>
              {setupData.qr_code && (
                <div className={styles.qrWrap}>
                  <img src={setupData.qr_code} alt="Authenticator QR code" />
                </div>
              )}

              <div>
                <span className={styles.hint}>Or enter this key manually:</span>
                <div style={{ marginTop: 6 }}>
                  <CodeBlock code={setupData.secret} wrap />
                </div>
              </div>

              {setupData.backup_codes.length > 0 && (
                <div>
                  <span className={styles.hint}>
                    Save these backup codes somewhere safe — each works once if you lose your device.
                  </span>
                  <div className={styles.backupCodes} style={{ marginTop: 6 }}>
                    {setupData.backup_codes.map((c) => (
                      <span key={c}>{c}</span>
                    ))}
                  </div>
                </div>
              )}

              <Field label="Verification code" required error={error ?? undefined}>
                {(props) => (
                  <Input
                    {...props}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="123456"
                    maxLength={6}
                    value={code}
                    invalid={!!error}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  />
                )}
              </Field>
            </div>
          )}

          {error && !setupData && (
            <Alert tone="danger" style={{ marginTop: 'var(--vn-space-3)' }}>
              {error}
            </Alert>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleVerify}
            disabled={!setupData || code.length !== 6 || verify.isPending}
          >
            {verify.isPending ? 'Verifying…' : 'Enable'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
